/**
 * ContractUpload — envio de UM documento PDF ao KYC do BO pelo Portal
 * (docs-cadastro, lote P3; SPEC v5.4 §Contrato, PLAN v1.6 §Contrato).
 *
 * Fluxo (presigned POST, ARQ1 = A): o browser NUNCA fala com o BO.
 *   1. POST /company-documents/presign {category, size, replaces_document_id?}
 *      ⇒ {url, fields, document_id}
 *   2. POST multipart direto no S3 (`url` + `fields`, o ficheiro por último)
 *   3. POST /company-documents/{document_id}/confirm {replaces_document_id?}
 *      — o MESMO alvo do presign, reenviado (PLAN v1.8 §Contrato, C1009722).
 *      Confirm falhado de forma retomável (rede, 5xx, 408, 429) ⇒ a nova
 *      escolha do MESMO ficheiro (mesmo SHA-256 do conteúdo) repete só este
 *      passo com o mesmo id (revisão Astra M3; C1009738); outro ficheiro, ou
 *      sem `crypto.subtle`, recomeça do passo 1.
 * O servidor é a guarda (PDF-only, ≤10 MB, limite de vigentes, escopo); a
 * validação local só poupa um round-trip — nunca substitui a do servidor.
 *
 * Token de escopo do pendente (SPEC Segurança 6; PLAN PE-2): quando existe,
 * vai em `Authorization: Bearer` (o backend já aceita o header como
 * alternativa ao cookie, deps.py). Vive só em memória do componente.
 *
 * COPY: toda string visível vem LITERAL da SPEC/PLAN ou da copy aprovada pelo
 * owner (`COPY_*`, C1009755).
 */
import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { SCOPE_TOKEN_FIELD } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";

// ---------------------------------------------------------------------------
// Copy aprovada pelo owner (C1009755: COPY-F4-PORTAL-PROPOSTA.md sha256
// 25e03b845c5b, 1.ª opção de cada linha), literal.
// ---------------------------------------------------------------------------
export const COPY_BOTAO_ENVIAR = "Enviar PDF";
export const COPY_BOTAO_SUBSTITUIR = "Substituir";
export const COPY_ERRO_SO_PDF = "Envie um arquivo em PDF.";
export const COPY_ERRO_TAMANHO_MAXIMO = "O arquivo está vazio ou tem mais de 10 MB.";
export const COPY_ERRO_ENVIO_ARQUIVO = "Não foi possível enviar o arquivo. Tente novamente.";
export const COPY_ENVIO_CONCLUIDO = "Documento enviado para análise";
/** Token de escopo expirado/inválido ⇒ convite a entrar de novo pelo /login (revisão P3 L6). */
export const COPY_SESSAO_EXPIRADA = "Sua sessão expirou. Entre novamente para enviar os documentos.";
/** Owner C1009767 (literais): rótulo do campo e erro de período em falta. */
export const COPY_PERIODO_ROTULO = "Período de referência";
export const COPY_PERIODO_OBRIGATORIO = "Escolha o período de referência do documento.";

// ---------------------------------------------------------------------------
// Contrato com o backend P1 (fonte da verdade: backend/app/routers/
// company_documents.py e auth.py). Conferido por companyDocuments.contract.test.jsx
// contra o código do backend: mudar um lado sem o outro ⇒ teste vermelho.
// ---------------------------------------------------------------------------
/** Prefixo do router (`APIRouter(prefix="/company-documents")`, montado em /api). */
export const COMPANY_DOCUMENTS_PATH = "/company-documents";
/** Campo de TOPO do token de escopo (definido em AuthContext, EAP-2). */
export { SCOPE_TOKEN_FIELD };
/** Campo de TOPO do GET da lista do pendente ("pf" | "pj"), acrescentado pelo Portal. */
export const PERSON_TYPE_FIELD = "person_type";
/** `detail.code` do Portal para token de escopo expirado/inválido/não pendente. */
export const SCOPE_INVALID_CODE = "pending_scope_invalid";

// ---------------------------------------------------------------------------
// Regras (dado, espelho da SPEC; o servidor decide)
// ---------------------------------------------------------------------------
/** SPEC R5 / Segurança 2: só PDF, até 10 MB (`content-length-range` 1..10 485 760). */
export const MAX_PDF_BYTES = 10485760;

/**
 * Período de referência (SPEC v5.7 R50; owner C1009756 + C1009767; forma do B2):
 * OBRIGATÓRIO em `dre`/`balancete`; «AAAA» (ano fechado) ou «AAAA (parcial Nº
 * tri)» do ano corrente (º = U+00BA). O texto do seletor vai ao servidor TAL
 * QUAL (sem normalizar); o servidor valida de novo (ano pelo relógio UTC dele).
 */
export const REFERENCE_PERIOD_CATEGORIES = ["dre", "balancete"];
export const REFERENCE_PERIOD_RE = /^(\d{4})( \(parcial [1-4]\u00ba tri\))?$/;
/** Anos fechados no seletor (C1009767: «os 5 últimos anos fechados»). */
export const REFERENCE_PERIOD_CLOSED_YEARS = 5;

export function requiresReferencePeriod(category) {
  return REFERENCE_PERIOD_CATEGORIES.includes(category);
}

/**
 * Opções do seletor, do mais recente ao mais antigo: o ano corrente (UTC) em 4
 * opções parciais por trimestre (4º → 1º) e os 5 últimos anos fechados.
 * `now` injetável (testes).
 */
export function referencePeriodOptions(now = new Date()) {
  const ano = now.getUTCFullYear();
  const parciais = [4, 3, 2, 1].map((t) => `${ano} (parcial ${t}\u00ba tri)`);
  const fechados = Array.from({ length: REFERENCE_PERIOD_CLOSED_YEARS }, (_, i) => String(ano - 1 - i));
  return [...parciais, ...fechados];
}

/** SPEC R4/R13/R35: vigentes por item — 1 nos obrigatórios únicos, ≤4 nos opcionais. */
export const MAX_VIGENTES_OPCIONAL = 4;

/**
 * Conjuntos por tipo do dono (SPEC «Conjuntos por tipo do dono no PCP»;
 * tabela do modelo único PF). `unico` = 1 vigente (enviar outro substitui).
 * Tabela de dados: nenhum `if` de categoria fora dela.
 */
export const DOCUMENT_SETS = {
  pj: [
    { category: "contrato_social", required: true, unico: true },
    { category: "dre", required: false, unico: false },
    { category: "balanco_patrimonial", required: false, unico: false },
    { category: "balancete", required: false, unico: false },
    { category: "faturamento", required: false, unico: false },
  ],
  pf: [
    { category: "identidade_socio", required: true, unico: true },
    { category: "comprovante_endereco", required: true, unico: true },
    { category: "declaracao_ir", required: false, unico: false },
    { category: "comprovante_renda", required: false, unico: false },
  ],
};

/**
 * Rótulos de UI — literais da SPEC v5.4: R1/R13 «Contrato social»; R3 «DRE»,
 * «Balanço», «Balancete», «Declaração/Comprovante de Faturamento» (Modelo de
 * dados: `balanco_patrimonial` = UI «Balanço», `faturamento` = UI
 * «Declaração/Comprovante de Faturamento»); tabela do modelo único PF e
 * «Por que reaproveitar» (rótulo do titular PF): «Documento com foto (RG ou
 * CNH)», «Comprovante de residência», «Declaração de IR (último exercício,
 * com recibo)», «Comprovante de renda».
 */
export const CATEGORY_LABELS = {
  contrato_social: "Contrato social",
  dre: "DRE",
  balanco_patrimonial: "Balanço",
  balancete: "Balancete",
  faturamento: "Declaração/Comprovante de Faturamento",
  identidade_socio: "Documento com foto (RG ou CNH)",
  comprovante_endereco: "Comprovante de residência",
  declaracao_ir: "Declaração de IR (último exercício, com recibo)",
  comprovante_renda: "Comprovante de renda",
};

/**
 * Tipo de pessoa DERIVADO do documento do cadastro (SPEC «Pessoa física —
 * modelo único»): 11 dígitos = PF, 14 = PJ; qualquer outro ⇒ `null`
 * (fail-closed — nunca «PJ por omissão»).
 */
export function personTypeFromDoc(doc) {
  const d = String(doc || "").replace(/\D/g, "");
  if (d.length === 11) return "pf";
  if (d.length === 14) return "pj";
  return null;
}

/** Validação local (espelho da do servidor). Devolve a mensagem ou `null`. */
export function validatePdf(file) {
  if (!file) return COPY_ERRO_SO_PDF;
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  if (!name.endsWith(".pdf") || type !== "application/pdf") return COPY_ERRO_SO_PDF;
  if (!(file.size > 0) || file.size > MAX_PDF_BYTES) return COPY_ERRO_TAMANHO_MAXIMO;
  return null;
}

// ---------------------------------------------------------------------------
// Cliente das rotas do Portal `/company-documents` (lote P1).
// Forma: a do intake do BO (PLAN §Contrato) repassada pelo Portal — ver
// PERGUNTAS no RELATORIO-P3 (P3-Q1..Q3).
// ---------------------------------------------------------------------------
/**
 * Com token de escopo: SÓ o Bearer, sem cookie de sessão (`withCredentials:
 * false`) — revisão P3 M1: sessão da empresa B + token do cadastro C nunca
 * pode agir como B. Sem token: sessão normal (cookie), como hoje.
 */
export function scopeConfig(scopeToken) {
  return scopeToken
    ? { headers: { Authorization: `Bearer ${scopeToken}` }, withCredentials: false }
    : {};
}

/** Erro de token de escopo expirado/inválido (401, ou 403 `pending_scope_invalid`). */
export function isScopeExpired(e) {
  const status = e?.response?.status;
  return status === 401
    || (status === 403 && e?.response?.data?.detail?.code === SCOPE_INVALID_CODE);
}

export async function listCompanyDocuments(scopeToken) {
  const { data } = await api.get(COMPANY_DOCUMENTS_PATH, scopeConfig(scopeToken));
  return data || {};
}

export async function requestDownloadUrl(documentId, scopeToken) {
  const { data } = await api.get(
    `${COMPANY_DOCUMENTS_PATH}/${encodeURIComponent(documentId)}/download`,
    scopeConfig(scopeToken));
  return data?.url;
}

async function presignUpload({ category, size, replacesDocumentId, referencePeriod, scopeToken }) {
  const body = { category, size };
  if (replacesDocumentId) body.replaces_document_id = replacesDocumentId;
  if (requiresReferencePeriod(category)) body.reference_period = referencePeriod;
  const { data } = await api.post(`${COMPANY_DOCUMENTS_PATH}/presign`, body, scopeConfig(scopeToken));
  return data;
}

async function postToStorage(url, fields, file) {
  const form = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => form.append(k, v));
  form.append("file", file); // S3 POST: o ficheiro é o último campo
  // Direto ao S3: sem Authorization e sem credenciais (revisão P3 L4).
  const resp = await fetch(url, { method: "POST", body: form, credentials: "omit" });
  if (!resp.ok) throw new Error("storage_upload_failed");
}

async function confirmUpload(documentId, scopeToken, replacesDocumentId) {
  // PLAN v1.8 §Contrato (C1009722 B1 P1): o alvo do presign é reenviado no confirm.
  const body = replacesDocumentId ? { replaces_document_id: replacesDocumentId } : {};
  const { data } = await api.post(
    `${COMPANY_DOCUMENTS_PATH}/${encodeURIComponent(documentId)}/confirm`, body,
    scopeConfig(scopeToken));
  return data;
}

/** `detail.code` do período (B2/C1009767) ⇒ mensagem do front. */
const REFERENCE_PERIOD_ERRORS = {
  reference_period_required: COPY_PERIODO_OBRIGATORIO,
  invalid_reference_period: COPY_ERRO_ENVIO_ARQUIVO,
  reference_period_not_allowed: COPY_ERRO_ENVIO_ARQUIVO,
};

/**
 * Texto do `detail` da API, ou a copy aprovada `fallback` quando a API não traz
 * `detail` (revisão Astra L1): `formatApiErrorDetail` devolve um genérico
 * não vazio para `detail` ausente, o que escondia a copy aprovada num `||`.
 */
export function detailOrCopy(detail, fallback) {
  if (detail == null || detail === "") return fallback;
  return formatApiErrorDetail(detail) || fallback;
}

/** Mensagem a mostrar: token de escopo expirado ⇒ convite ao /login; senão `detail`
 *  (sem `detail` ⇒ copy aprovada nº 20, `COPY_ERRO_ENVIO_ARQUIVO`). */
export function apiErrorMessage(e, scopeToken) {
  if (scopeToken && isScopeExpired(e)) return COPY_SESSAO_EXPIRADA;
  const periodo = e?.response?.status === 400 && REFERENCE_PERIOD_ERRORS[e?.response?.data?.detail?.code];
  if (periodo) return periodo;
  return detailOrCopy(e?.response?.data?.detail, COPY_ERRO_ENVIO_ARQUIVO);
}

/**
 * Falha do confirm DEPOIS de o POST ao S3 ter tido sucesso (revisão Astra M3):
 * leva o `document_id` já carregado para a retomada repetir SÓ o confirm.
 * `retryable` = o mesmo id pode ser confirmado de novo (ver `isConfirmRetryable`).
 */
export class ConfirmFailedError extends Error {
  constructor(message, documentId, retryable) {
    super(message);
    this.name = "ConfirmFailedError";
    this.documentId = documentId;
    this.retryable = retryable;
  }
}

/**
 * Confirm retomável com o MESMO `document_id`: sem resposta (rede/timeout),
 * 5xx (ex.: 503 leitura S3 no BO), 408 ou 429. Qualquer outro 4xx é recusa do
 * servidor sobre aquele documento ⇒ a próxima tentativa recomeça do presign.
 */
export function isConfirmRetryable(e) {
  const status = e?.response?.status;
  if (!status) return true;
  return status >= 500 || status === 408 || status === 429;
}

/**
 * Só o passo 3 (confirm) de um `document_id` já enviado ao S3, com o MESMO
 * `replacesDocumentId` usado no presign desse id.
 */
export async function confirmCompanyDocument(documentId, scopeToken, replacesDocumentId) {
  try {
    return await confirmUpload(documentId, scopeToken, replacesDocumentId);
  } catch (e) {
    throw new ConfirmFailedError(apiErrorMessage(e, scopeToken), documentId, isConfirmRetryable(e));
  }
}

/**
 * Envio completo de 1 PDF; lança Error com a mensagem a mostrar. Falha no
 * confirm ⇒ `ConfirmFailedError` com o `document_id` (retomada sem novo upload).
 */
export async function uploadCompanyDocument({
  file, category, replacesDocumentId, referencePeriod, scopeToken,
}) {
  const invalid = validatePdf(file);
  if (invalid) throw new Error(invalid);
  if (requiresReferencePeriod(category)) {
    if (!referencePeriod) throw new Error(COPY_PERIODO_OBRIGATORIO);
    if (!REFERENCE_PERIOD_RE.test(referencePeriod)) throw new Error(COPY_ERRO_ENVIO_ARQUIVO);
  }
  let presign;
  try {
    presign = await presignUpload({
      category, size: file.size, replacesDocumentId, referencePeriod, scopeToken,
    });
  } catch (e) {
    throw new Error(apiErrorMessage(e, scopeToken));
  }
  if (!presign?.url || !presign?.document_id) throw new Error(COPY_ERRO_ENVIO_ARQUIVO);
  try {
    await postToStorage(presign.url, presign.fields, file);
  } catch {
    throw new Error(COPY_ERRO_ENVIO_ARQUIVO);
  }
  return confirmCompanyDocument(presign.document_id, scopeToken, replacesDocumentId);
}

/** Bytes do ficheiro (`Blob.arrayBuffer`, ou `FileReader` onde não existir). */
function readFileBytes(file) {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/**
 * SHA-256 (hex) do CONTEÚDO do ficheiro (revisões Astra/Sol C1009738): os
 * metadados (nome, tamanho, `lastModified`, MIME) não identificam o ficheiro.
 * Sem `crypto.subtle` ou com erro ⇒ `null` (o chamador NUNCA reutiliza).
 */
export async function contentSha256(file) {
  const subtle = globalThis.crypto && globalThis.crypto.subtle;
  if (!subtle || typeof subtle.digest !== "function") return null;
  try {
    const digest = await subtle.digest("SHA-256", await readFileBytes(file));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

/**
 * Identidade do envio para a retomada: mesma categoria/alvo E o mesmo CONTEÚDO
 * (SHA-256). Sem hash ⇒ `null` = não reutilizável (fail-closed: recomeça do presign).
 */
function uploadKey(file, category, replacesDocumentId, sha256, referencePeriod) {
  if (!sha256) return null;
  return JSON.stringify([category, replacesDocumentId || null, referencePeriod || null,
    file.name, file.size, file.lastModified, file.type, sha256]);
}

/**
 * Botão de envio de 1 documento. `replacesDocumentId` = alvo da substituição
 * (opcionais com 4 vigentes; PLAN §Contrato). Sem operação de «retirar»
 * (DOC14): este componente só envia. `label` (KYC «Não enviado», C1014087 U2)
 * troca SÓ o rótulo do botão; ausente ⇒ «Enviar PDF»/«Substituir» como hoje.
 */
export default function ContractUpload({
  category, scopeToken, replacesDocumentId, referencePeriod, onUploaded, disabled, replace = false,
  testid, label,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Revisão Astra M3: confirm falhado (retomável) depois do POST ao S3 ⇒
  // `{ key, documentId, replacesDocumentId }`. A nova escolha do MESMO ficheiro
  // para a MESMA categoria/alvo repete só o confirm desse id, com o alvo usado
  // no presign dele (PLAN v1.8); outro ficheiro recomeça.
  const pendingConfirmRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // permite escolher o mesmo ficheiro de novo
    if (!file) return;
    setBusy(true);
    const pending = pendingConfirmRef.current;
    pendingConfirmRef.current = null;
    let key = null;
    try {
      // Hash calculado UMA vez por ficheiro selecionado (C1009738).
      key = uploadKey(file, category, replacesDocumentId, await contentSha256(file), referencePeriod);
      if (pending && key !== null && pending.key === key) {
        await confirmCompanyDocument(pending.documentId, scopeToken, pending.replacesDocumentId);
      } else {
        await uploadCompanyDocument({ file, category, replacesDocumentId, referencePeriod, scopeToken });
      }
      toast.success(COPY_ENVIO_CONCLUIDO);
      if (onUploaded) await onUploaded();
    } catch (err) {
      if (err instanceof ConfirmFailedError && err.retryable && key !== null) {
        pendingConfirmRef.current = { key, documentId: err.documentId, replacesDocumentId };
      }
      toast.error(err.message || COPY_ERRO_ENVIO_ARQUIVO);
    } finally {
      setBusy(false);
    }
  };

  const id = testid || `doc-upload-${category}${replacesDocumentId ? `-${replacesDocumentId}` : ""}`;
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={onPick}
        data-testid={`${id}-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || busy}
        onClick={() => inputRef.current && inputRef.current.click()}
        data-testid={id}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
        {label || (replace ? COPY_BOTAO_SUBSTITUIR : COPY_BOTAO_ENVIAR)}
      </Button>
    </>
  );
}
